import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-[color:var(--lilia-on-primary)] hover:bg-primary-deep",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          // Text defaults to foreground rather than inheriting — without
          // this, outline buttons inside muted-context containers rendered
          // as low-contrast gray-on-gray (Teams page Manage button).
          // bg-card is soft paper in both light + dark, giving a subtle
          // lift against the bone page background.
          "border border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground",
        // Every real action that is not the view's one primary (Olivia,
        // tables-modern turn 3, 3b): the accent's wash, a 32 % accent edge,
        // the accent as ink; the hover deepens to 14 %.
        tonal:
          "border border-[color-mix(in_srgb,var(--lilia-primary)_32%,transparent)] bg-[var(--lilia-primary-subtle)] text-[color:var(--lilia-primary-ink)] hover:bg-[color-mix(in_srgb,var(--lilia-primary)_14%,var(--lilia-surface))]",
        subtle:
          "hover:bg-accent hover:text-accent-foreground",
        ghost:
          "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        xs: "h-7 rounded-md px-2.5 text-xs",
        "icon-sm": "h-8 w-8",
        "icon-xs": "h-6 w-6",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps };
